import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchInput } from './SearchInput';

export interface FilterOption {
  value: string;
  label: string;
}

export interface TableFilter {
  id: string;
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  width?: string;
}

export interface TableFiltersProps {
  /**
   * Search value
   */
  search?: string;
  /**
   * Search onChange handler
   */
  onSearchChange?: (value: string) => void;
  /**
   * Search placeholder text
   */
  searchPlaceholder?: string;
  /**
   * Array of filter configurations
   */
  filters?: TableFilter[];
  /**
   * Additional custom filter components to render
   */
  customFilters?: React.ReactNode;
}

/**
 * Standardized table filters component with search and select filters
 * Based on the Locations page design pattern
 */
export function TableFilters({
  search,
  onSearchChange,
  searchPlaceholder = 'Search...',
  filters = [],
  customFilters,
}: TableFiltersProps) {
  return (
    <div className="flex flex-wrap items-center gap-4">
      {/* Search Input */}
      {onSearchChange && (
        <SearchInput
          value={search}
          onChange={onSearchChange}
          placeholder={searchPlaceholder}
          className="flex-1 min-w-[200px]"
        />
      )}

      {/* Select Filters */}
      {filters.map((filter) => (
        <Select
          key={filter.id}
          value={filter.value}
          onValueChange={(v) => filter.onChange(v === 'all' ? '' : v)}
        >
          <SelectTrigger className={filter.width || 'w-[150px]'}>
            <SelectValue placeholder={filter.placeholder || filter.label} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All {filter.label}</SelectItem>
            {filter.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ))}

      {/* Custom Filters */}
      {customFilters}
    </div>
  );
}
